import { createIDTagging, useIDTagging, useTranslation } from '@msdk/common'
import {
  Checkbox,
  Collapse,
  ExpandLessIcon,
  ExpandMoreIcon,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@msdk/ui'
import React, { SyntheticEvent, useState } from 'react'

import { FieldEntityPair } from '../field-entity-pair'
import { NodeGroup } from '../node'
import { Item } from './Item'

interface Props {
  item: NodeGroup
  selected: FieldEntityPair[]
  onToggle: (item: FieldEntityPair) => void
  onToggleAll: (items: FieldEntityPair[]) => void
  multiselect: boolean
}

export function Group({ item, selected, onToggle, multiselect, onToggleAll }: Props) {
  const [open, setOpen] = useState<boolean>(true)
  const t = useTranslation()
  const idTaggingEnabled = useIDTagging()
  const description = idTaggingEnabled
    ? createIDTagging(item.microservice, item.entityName, item.idml)
    : t(item.idml, item.tDescription ?? item.description)

  const handleOpen = () => {
    setOpen(!open)
  }

  const isChecked = () => {
    return item.allEquals(selected) && item.children.length > 0
  }

  const isIntermidiate = () => {
    return item.someEquals(selected)
  }

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const onItemToggle = (item: FieldEntityPair) => {
    onToggle(item)
  }

  const handleToggleAll = (e: SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    const toToggle = item.toFieldEntityPairs()
    onToggleAll(toToggle)
  }

  return (
    <>
      <ListItemButton dense onClick={handleOpen}>
        {multiselect && (
          <ListItemIcon>
            <Checkbox
              edge="start"
              indeterminate={isIntermidiate()}
              checked={isChecked()}
              tabIndex={-1}
              disableRipple
              onClick={handleToggleAll}
            />
          </ListItemIcon>
        )}
        <ListItemText primary={description} />
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List key={item.value} component="div">
          {item.children.map((n) => {
            if (n instanceof NodeGroup)
              return (
                <Group
                  item={n}
                  key={n.value}
                  selected={selected}
                  onToggle={onItemToggle}
                  onToggleAll={(items) => onToggleAll(items)}
                  multiselect={multiselect}
                />
              )
            else return <Item item={n} key={n.value} selected={selected} onToggle={onItemToggle} />
          })}
        </List>
      </Collapse>
    </>
  )
}
